import { buildReviewThemesNamespace } from "./reviewThemes.ts";
import type { en } from "./en.ts";

export const fr: typeof en = {
    Navigation: {
        analytics: {
            label: "Analytique",
            description: "Engagement, rapports et tendances",
            engagement: {
                label: "Engagement",
                main: {
                    label: "Engagement",
                    overview: { label: "Aperçu" },
                    metrics: { label: "Métriques" },
                    "question-results": { label: "Résultats des questions" },
                    heatmap: { label: "Vue comparative du sondage éclair" }
                },
                "metric-detail": { label: "Détails de la métrique" },
                feedback: {
                    label: "Feedback",
                    overview: { label: "Aperçu" }
                },
                "manager-impact": {
                    label: "Impact des gestionnaires",
                    overview: { label: "Impact des gestionnaires" }
                }
            },
            performance: {
                label: "Performance",
                main: {
                    label: "Évaluations de performance",
                    overview: { label: "Scores" },
                    calibration: { label: "Calibration" },
                    themes: { label: "Thèmes" }
                }
            },
            recognition: {
                label: "Reconnaissance",
                main: {
                    label: "Bonnes Vibes",
                    overview: { label: "Aperçu" }
                }
            },
            reports: {
                label: "Rapports",
                engagement: { label: "Engagement" },
                feedback: { label: "Feedback" },
                "good-vibes": { label: "Bonnes Vibes" }
            },
            meetings: {
                label: "Réunions",
                "one-on-ones": { label: "1:1" }
            },
            workforce: {
                label: "Main-d'œuvre",
                retention: { label: "Rétention" },
                workforce: { label: "Analyse de la main-d'œuvre" }
            },
            custom: {
                label: "Personnalisé",
                custom: { label: "Rapports personnalisés" }
            }
        }
    },
    ManagerHealthDonut: {
        title: "Portrait des gestionnaires",
        subtitle: "Répartition des gestionnaires selon leur niveau d'impact.",
        great: "Excellent",
        needsWork: "À améliorer",
        info: "La proportion de vos gestionnaires jugés <strong>Excellent</strong> ou <strong>À améliorer</strong>, selon le score d'impact global de chaque gestionnaire.",
        empty: "Aucun gestionnaire de cette portée n'a encore de score d'impact."
    },
    ManagerImpactMetricInfo: {
        label: "À propos de {{metric}}"
    },
    ManagerImpactHrPage: {
        title: "Impact des gestionnaires",
        betaBadge: "Bêta",
        subtitle: "Identifiez les gestionnaires qui ont besoin de soutien et mesurez l'impact de la plateforme.",
        liveRegion: {
            loading: "Chargement des scores d'impact des gestionnaires"
        },
        empty: {
            title: "Aucun score d'impact des gestionnaires pour l'instant",
            description: {
                directReports: "Aucun de vos rapports directs n'a encore de score d'impact. Passez à Tous les gestionnaires pour voir toute votre organisation.",
                all: "Aucun gestionnaire de votre organisation n'a encore de score d'impact."
            }
        },
        scope: {
            ariaLabel: "Portée",
            directReports: "Mes rapports directs",
            all: "Tous les gestionnaires"
        },
        summaryCards: {
            overallImpact: {
                title: "Impact global des gestionnaires",
                formula: "Pratique (60 %) x Résultats (40 %)",
                status: {
                    great: "Excellent",
                    needsWork: "À améliorer"
                },
                practice: {
                    label: "Pratique",
                    description: "Comportements : 1:1, feedback, reconnaissance, objectifs"
                },
                outcome: {
                    label: "Résultats",
                    description: "Résultats : engagement, rétention, performance"
                },
                empty: "Pas encore assez de données pour calculer un score global."
            }
        },
        leaderboard: {
            title: "Classement des gestionnaires",
            subtitle: "Classé par score d'impact, triable par pilier.",
            ariaLabel: "Classement des gestionnaires",
            scoreUnavailable: "Score indisponible",
            columns: {
                manager: "Gestionnaire",
                participationRate: "Taux de participation",
                impact: "Impact",
                practice: "Pratique",
                outcome: "Résultats"
            },
            columnInfo: {
                participationRate: "Pourcentage des membres qui ont répondu à un sondage au cours des 30 derniers jours pour ce gestionnaire.",
                impact: "Le score global d'un gestionnaire, qui combine ses pratiques de gestion (60 %) et les résultats de son équipe (40 %).",
                practice: "Les habitudes de gestion qui relèvent du gestionnaire : réponses au feedback, soumission des évaluations, mises à jour des objectifs et reconnaissance donnée. 60 % de l'impact.",
                outcome: "Les résultats vécus par l'équipe du gestionnaire : engagement, relation avec le gestionnaire, résultats des évaluations et atteinte des objectifs. 40 % de l'impact."
            },
            pagination: {
                showing: "Affichage de {{start}}–{{end}} sur {{total}}",
                previous: "Page précédente",
                next: "Page suivante"
            },
            liveRegion: {
                sortedAscending: "Trié par {{column}} de façon croissante",
                sortedDescending: "Trié par {{column}} de façon décroissante"
            }
        },
        feedback: {
            heading: "Comment est ce rapport ?",
            description: "Ce rapport est nouveau et encore en bêta. Un retour rapide de votre part influence ce que nous livrons.",
            accurate: "Ce rapport est utile",
            inaccurate: "Ce rapport n'est pas utile",
            placeholder: "Quelque chose à changer ? Optionnel.",
            submit: "Envoyer le retour",
            thanks: "Merci, nous avons enregistré votre retour."
        }
    },
    EngagementFiltersHeading: {
        subtitle: "Ce que vos employés ressentent, à travers vos indicateurs d'engagement.",
        heatmapScoreModeDisabledReason: "Le mode Score affiche la valeur de chaque équipe à la date sélectionnée, et non sur une période. Passez au mode Variation pour comparer sur une période."
    },
    EngagementRating: {
        critical: "Critique",
        veryLow: "Très faible",
        low: "Faible",
        good: "Bon",
        veryGood: "Très bon",
        great: "Excellent",
        amazing: "Exceptionnel"
    },
    EngagementOverviewPage: {
        liveRegion: {
            loading: "Chargement de l'aperçu de l'engagement",
            loadingInsight: "Chargement de la synthèse de l'engagement"
        },
        sectionError: {
            insight: "Impossible de charger la synthèse de l'engagement pour l'instant.",
            history: "Impossible de charger les scores d'engagement pour l'instant.",
            enps: "Impossible de charger l'eNPS pour l'instant.",
            participation: "Impossible de charger le taux de participation pour l'instant."
        },
        sectionErrorReassurance: "Veuillez réessayer plus tard.",
        retry: "Réessayer",
        retrying: "Nouvelle tentative…",
        timeFrame: {
            relativeLabel: {
                "7d": "7 jours",
                "30d": "30 jours",
                "3m": "3 mois",
                "6m": "6 mois",
                quarter: "ce trimestre",
                year: "cette année",
                allTime: "tout le temps"
            }
        }
    },
    EngagementOverviewCard: {
        title: "Score d'engagement global",
        delta_one: "{{sign}}{{value}} pt {{period}}",
        delta_other: "{{sign}}{{value}} pts {{period}}",
        deltaZero: "Aucun changement {{period}}",
        deltaPeriod: {
            "7d": "vs les 7 jours précédents",
            "30d": "vs les 30 jours précédents",
            "3m": "vs les 3 mois précédents",
            "6m": "vs les 6 mois précédents",
            quarter: "vs le trimestre précédent",
            year: "vs l'année précédente",
            allTime: "vs la période précédente"
        },
        notEnoughResponses: "Pas assez de réponses",
        notEnoughResponsesAnonymity: "Moins de 3 réponses ont été recueillies durant cette période. Les résultats sont masqués pour protéger l'anonymat.",
        scoreAriaLabel: "Score d'engagement",
        scoreNotAvailableAriaLabel: "Score d'engagement non disponible : pas assez de réponses",
        outOf: "sur {{max}}",
        valueLabel: "{{score}} sur {{max}}",
        sparkline: {
            myTeam: "Mon équipe",
            directReports: "Subordonnés directs",
            company: "Entreprise"
        },
        companyAverage: {
            label: "Moyenne de l'entreprise",
            deltaValue_one: "{{sign}}{{value}} pt",
            deltaValue_other: "{{sign}}{{value}} pts",
            notAvailable: "Non disponible"
        }
    },
    ParticipationGauge: {
        countLabel: "{{responded}} / {{invited}}"
    },
    ParticipationOverviewCard: {
        title: "Taux de participation",
        tooltipLabel: "À propos du taux de participation",
        tooltip: "Il s'agit du pourcentage de membres ayant répondu à un sondage au cours de la période sélectionnée.",
        delta_one: "{{sign}}{{value}} pt {{period}}",
        delta_other: "{{sign}}{{value}} pts {{period}}",
        deltaZero: "Aucun changement {{period}}",
        deltaPeriod: {
            "7d": "vs les 7 jours précédents",
            "30d": "vs les 30 jours précédents",
            "3m": "vs les 3 mois précédents",
            "6m": "vs les 6 mois précédents",
            quarter: "vs le trimestre précédent",
            year: "vs l'année précédente",
            allTime: "vs la période précédente"
        },
        rateAriaLabel: "Taux de participation {{value}}, {{responded}} personnes sur {{invited}} ont répondu",
        noInvitesBandLabel: "Aucune donnée",
        noInvitesNote: "Aucun sondage n'a été envoyé durant cette période.",
        sparkline: {
            myTeam: "Mon équipe",
            directReports: "Subordonnés directs",
            company: "Entreprise",
            yAxisLabel: "Taux de participation",
            ariaLabel: "Graphique du taux de participation sur {{period}}"
        },
        companyAverage: {
            label: "Moyenne de l'entreprise",
            deltaValue_one: "{{sign}}{{value}} pt",
            deltaValue_other: "{{sign}}{{value}} pts",
            notAvailable: "Non disponible"
        }
    },
    EnpsOverviewCard: {
        title: "Score Net Promoteur des employés",
        tooltipLabel: "À propos de l'eNPS",
        tooltip: "Les employés évaluent leur probabilité de recommander ce lieu de travail sur une échelle de 0 à 10. Les promoteurs obtiennent 9–10, les passifs 7–8, les détracteurs 0–6.",
        intro: "Les scores reflètent les 90 derniers jours de réponses, indépendamment de la plage de dates sélectionnée ci-dessus.",
        learnMore: "En savoir plus",
        offBanner: "Les sondages eNPS sont désactivés. Affichage du dernier score du {{date}}.",
        offBannerDateless: "Les sondages eNPS sont désactivés. Affichage du dernier score disponible.",
        offBannerNoScore: "Les sondages eNPS sont désactivés pour votre organisation.",
        showDistribution: "Afficher la distribution",
        hideDistribution: "Masquer la distribution",
        notEnoughResponsesAnonymity: "Moins de 3 réponses ont été recueillies durant cette période. Les résultats sont masqués pour protéger l'anonymat.",
        sparkline: {
            myTeam: "Mon équipe",
            directReports: "Subordonnés directs",
            company: "Entreprise"
        }
    },
    EngagementInsightCard: {
        notEnoughData: "Pas assez de données pour générer une synthèse pour cette période.",
        seeMore: "Voir plus",
        seeLess: "Voir moins",
        sourcesLabel: "Sources",
        sourceChips: {
            pulse: "Réponses au sondage Pulse",
            feedback: "Rétroaction"
        },
        feedback: {
            accuracyPrompt: "Est-ce utile?",
            accurate: "Exact",
            inaccurate: "Inexact",
            thanks: "Merci pour votre commentaire!",
            reasonDialog: {
                title: "Dites-nous en plus",
                description: "Qu'est-ce qui était inexact dans cette synthèse?",
                placeholder: "Partagez des détails (facultatif)",
                skip: "Ignorer",
                submit: "Envoyer"
            }
        }
    },
    EngagementPage: {
        title: "Engagement",
        empty: "Rien à afficher pour le moment.",
        liveRegion: {
            loading: "Chargement des métriques"
        }
    },
    EngagementPageActions: {
        analyze: "Analyser l'engagement",
        analyzePrompt: "Analyse les résultats d'engagement et dis-moi sur quoi me concentrer.",
        error: "Impossible d'analyser l'engagement. Veuillez réessayer.",
        exportCsv: {
            metrics: "Exporter les métriques en CSV",
            questionResults: "Exporter les résultats des questions en CSV",
            heatmap: "Exporter la carte de chaleur en CSV"
        },
        exportExcel: {
            metrics: "Exporter les métriques en Excel",
            questionResults: "Exporter les résultats des questions en Excel",
            heatmap: "Exporter la carte de chaleur en Excel"
        },
        exportFailed: "Impossible de préparer votre exportation. Veuillez réessayer.",
        actions: "Options du rapport Engagement",
        sendReminder: "Envoyer un rappel de sondage",
        manageSurveySettings: "Gérer les paramètres du sondage",
        createCustomPresentation: {
            Pdf: "Créer un rapport personnalisé",
            Pptx: "Créer une présentation personnalisée"
        },
        createCustomPresentationPrompt: {
            Pdf: "Aide-moi à créer un rapport PDF personnalisé basé sur les données d'engagement pour {{scope}}, couvrant {{timeFrame}}.",
            Pptx: "Aide-moi à créer une présentation personnalisée basée sur les données d'engagement pour {{scope}}, couvrant {{timeFrame}}."
        },
        createCustomPresentationFailed: "Impossible de récupérer les données de votre présentation. Veuillez réessayer.",
        scopePhrase: {
            organizationFallback: "l'entreprise",
            "reporting-line": "mon équipe",
            "direct-reports": "mes subordonnés directs"
        },
        timeFramePhrase: {
            "7d": "les 7 derniers jours",
            "30d": "les 30 derniers jours",
            "3m": "les 3 derniers mois",
            "6m": "les 6 derniers mois",
            quarter: "ce trimestre",
            year: "cette année",
            allTime: "tout le temps"
        },
        surveySettingsRedirect: {
            eyebrow: "À noter",
            title: "Un petit détour",
            body: "Les paramètres du sondage ne sont pas encore disponibles dans la nouvelle application. Nous vous redirigerons vers Officevibe pour les gérer. Rien ne sera perdu.",
            cancel: "Annuler",
            continue: "Continuer dans Officevibe"
        }
    },
    SendSurveyReminderDialog: {
        title: "Envoyer un rappel de sondage",
        count_one: "{{count}} personne n'a pas encore répondu au sondage.",
        count_other: "{{count}} personnes n'ont pas encore répondu au sondage.",
        countLoading: "Vérification des personnes n'ayant pas répondu…",
        countUnavailable: "Impossible de vérifier qui n'a pas encore répondu.",
        messageLabel: "Message personnalisé (facultatif)",
        messagePlaceholder: "Écrivez un message…",
        cancel: "Annuler",
        send: "Envoyer le rappel",
        successToast: "Rappel de sondage envoyé",
        errorToast: "Impossible d'envoyer le rappel de sondage. Veuillez réessayer."
    },
    EngagementMetricsPage: {
        title: "Métriques",
        description: "Les métriques d'engagement basées sur des données scientifiques derrière le score global.",
        liveRegion: {
            loading: "Chargement des métriques"
        },
        sectionError: "La liste des métriques n'a pas pu être chargée",
        sectionErrorReassurance: "Veuillez réessayer plus tard.",
        retry: "Réessayer",
        retrying: "Nouvelle tentative…"
    },
    EngagementQuestionResultsPage: {
        liveRegion: {
            loading: "Chargement des questions"
        },
        standardQuestions: {
            heading: "Résultats des questions standard",
            description: "Résultats des questions scientifiquement validées de votre sondage éclair.",
            tableAriaLabel: "Questions standard",
            manageQuestions: "Gérer les questions",
            manageQuestionsRedirect: {
                eyebrow: "À noter",
                title: "Un petit détour",
                body: "La gestion des questions n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Officevibe pour les gérer. Rien ne sera perdu.",
                cancel: "Annuler",
                continue: "Continuer dans Officevibe"
            }
        },
        customQuestions: {
            heading: "Résultats des questions personnalisées",
            description: "Résultats des questions ajoutées par votre organisation.",
            tableAriaLabel: "Questions personnalisées",
            manageQuestions: "Gérer les questions",
            manageQuestionsRedirect: {
                eyebrow: "À noter",
                title: "Un petit détour",
                body: "La gestion des questions personnalisées n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Officevibe pour les gérer. Rien ne sera perdu.",
                cancel: "Annuler",
                continue: "Continuer dans Officevibe"
            },
            addCustomQuestion: "Ajouter une question personnalisée",
            newBadge: "Nouveau",
            addCustomQuestionRedirect: {
                eyebrow: "À noter",
                title: "Un petit détour",
                body: "L'ajout de questions personnalisées n'est pas encore disponible dans la nouvelle application. Nous vous redirigerons vers Officevibe pour en ajouter une. Rien ne sera perdu.",
                cancel: "Annuler",
                continue: "Continuer dans Officevibe"
            }
        },
        sectionError: "La liste des questions n'a pas pu être chargée",
        sectionErrorReassurance: "Veuillez réessayer plus tard.",
        retry: "Réessayer",
        retrying: "Nouvelle tentative…"
    },
    EngagementMetricDetailNotFound: {
        heading: "Métrique introuvable",
        body: "Cette métrique n'existe pas ou a peut-être été supprimée.",
        backToMetrics: "Retour aux métriques"
    },
    EngagementMetricDetailPage: {
        liveRegion: {
            loading: "Chargement de la métrique"
        },
        backToMetrics: "Retour aux métriques",
        backToQuestionResults: "Retour aux résultats des questions",
        tooltipLabel: "À propos de {{metric}}",
        questionsTableAriaLabel: "Questions de {{metric}}",
        timeFrame: {
            relativeLabel: {
                "7d": "7 jours",
                "30d": "30 jours",
                "3m": "3 mois",
                "6m": "6 mois",
                quarter: "ce trimestre",
                year: "cette année",
                allTime: "tout le temps"
            }
        }
    },
    CustomQuestionDetailPage: {
        liveRegion: {
            loading: "Chargement de la question"
        },
        backToQuestionResults: "Retour aux résultats des questions",
        eyebrow: "Question personnalisée",
        status: {
            active: "Active",
            paused: "En pause"
        },
        meta: {
            updated: "Mis à jour {{date}}",
            createdBy: "Créée par {{name}}"
        },
        timeFrame: {
            relativeLabel: {
                "7d": "7 jours",
                "30d": "30 jours",
                "3m": "3 mois",
                "6m": "6 mois",
                quarter: "ce trimestre",
                year: "cette année",
                allTime: "tout le temps"
            }
        },
        periodLabel: {
            "7d": "7 jours",
            "30d": "30 jours",
            "3m": "3 mois",
            "6m": "6 mois",
            quarter: "le trimestre",
            year: "l'année",
            allTime: "tout l'historique"
        },
        deltaPeriod: {
            "7d": "vs les 7 jours précédents",
            "30d": "vs les 30 jours précédents",
            "3m": "vs les 3 mois précédents",
            "6m": "vs les 6 mois précédents",
            quarter: "vs le trimestre précédent",
            year: "vs l'année précédente",
            allTime: "vs la période précédente"
        },
        delta_one: "{{sign}}{{value}} pt {{period}}",
        delta_other: "{{sign}}{{value}} pts {{period}}",
        deltaZero: "Aucun changement {{period}}",
        notEnoughResponses: "Pas assez de réponses",
        scoreAriaLabel: "Score de la question",
        scoreNotAvailableAriaLabel: "Score de la question non disponible : pas assez de réponses",
        outOf: "sur {{max}}",
        valueLabel: "{{score}} sur {{max}}",
        sparkline: {
            myTeam: "Mon équipe",
            directReports: "Subordonnés directs",
            company: "Entreprise",
            yAxisLabel: "Score"
        },
        latestScore: {
            title: "Dernier score"
        },
        companyAverage: {
            label: "Score de l'entreprise",
            deltaValue_one: "{{sign}}{{value}} pt",
            deltaValue_other: "{{sign}}{{value}} pts",
            deltaZero: "aucun changement {{period}}",
            notAvailable: "Non disponible"
        },
        participation: {
            title: "Taux de participation sur {{period}}",
            rate: "{{rate}} %",
            noData: "–"
        },
        responses: {
            title: "Réponses"
        }
    },
    CustomQuestionResponsesDistribution: {
        noResponses: "Aucune réponse pour le moment.",
        percentage: "{{percentage}} %",
        people_one: "{{count}} personne",
        people_other: "{{count}} personnes"
    },
    CustomQuestionDetailNotFound: {
        heading: "Question personnalisée introuvable",
        body: "Cette question personnalisée n'existe pas ou a peut-être été supprimée.",
        backToQuestionResults: "Retour aux résultats des questions"
    },
    EngagementVariationBadge: {
        increased: "En hausse",
        decreased: "En baisse",
        noComparison: "Aucune comparaison disponible"
    },
    EngagementMetricsTable: {
        ariaLabel: "Métriques d'engagement",
        noScore: "Aucun score",
        tooltipLabel: "À propos de {{metric}}",
        deltaValue_one: "{{value}} pt",
        deltaValue_other: "{{value}} pts",
        columns: {
            metric: "Métrique",
            score: "Score",
            change: "Variation",
            changeSubtitle: "sur {{period}}",
            details: "Ouvrir les détails de la métrique"
        },
        periodLabel: {
            "7d": "7 jours",
            "30d": "30 jours",
            "3m": "3 mois",
            "6m": "6 mois",
            quarter: "le trimestre",
            year: "l'année",
            allTime: "tout l'historique"
        },
        liveRegion: {
            sortChanged: "Trié par {{column}} {{direction}}",
            ascending: "croissant",
            descending: "décroissant",
            columns: {
                name: "Métrique",
                score: "Score",
                scoreDelta: "Variation"
            }
        }
    },
    EngagementPeriodToolbar: {
        disabledInfoLabel: "Pourquoi ce filtre est-il désactivé?",
        timeFrame: {
            ariaLabel: "Période",
            clearAriaLabel: "Effacer le filtre de période",
            "7d": "7 derniers jours",
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année",
            allTime: "Depuis toujours"
        }
    },
    FeedbackOverviewPage: {
        audienceRefusal: {
            segmentNotVisible: {
                title: "Ce segment ne vous est plus accessible",
                description: "Vous n'avez plus accès à ce segment. Choisissez-en un autre ou retirez le filtre pour voir le feedback."
            },
            handPickedSegment: {
                title: "Les segments composés manuellement ne sont pas pris en charge ici",
                description: "Le feedback ne peut être restreint qu'aux segments basés sur des règles. Choisissez un segment basé sur des règles ou retirez le filtre."
            },
            brokenRules: {
                title: "Ce segment contient des règles que nous ne pouvons pas lire",
                description: "Certaines règles de ce segment ne sont pas prises en charge, le feedback ne peut donc pas y être restreint. Modifiez le segment ou retirez le filtre."
            },
            unsupportedCapability: {
                title: "Ce segment n'est pas encore pris en charge ici",
                description: "Le feedback ne peut pas être restreint à un segment qui correspond à n'importe quelle règle. Passez-le à toutes les règles."
            }
        }
    },
    FeedbackPeriodToolbar: {
        ariaLabel: "Période",
        clearAriaLabel: "Effacer le filtre de période",
        timeFrames: {
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année"
        },
        custom: "Période personnalisée",
        rangeLabel: "Date de début et de fin",
        invalidRange: "La date de fin ne peut pas précéder la date de début.",
        rangeTooLong: "Une période personnalisée ne peut pas dépasser 3 ans."
    },
    GoodVibesTimeFrameSelect: {
        ariaLabel: "Période",
        clearAriaLabel: "Effacer le filtre de période",
        timeFrames: {
            "7d": "7 derniers jours",
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année"
        }
    },
    GoodVibesVisibilitySelect: {
        ariaLabel: "Visibilité",
        clearAriaLabel: "Effacer le filtre de visibilité",
        triggerPrefix: "Visibilité :",
        visibilities: {
            all: "Toutes les Bonnes Vibes",
            public: "Publiques",
            direct: "Directes"
        }
    },
    FeedbackFilterChip: {
        chipLabel: "{{dimension}} : {{value}}",
        clearFilter: "Effacer le filtre",
        removeFilter: "Retirer le filtre {{dimension}}",
        loading: "Chargement…"
    },
    FeedbackFilterBar: {
        addFilters: "Ajouter des filtres",
        clearAllFilters: "Effacer tous les filtres",
        dimensions: {
            sentiment: "Sentiment",
            metricId: "Métrique",
            feedbackType: "Type"
        },
        errors: {
            metricId: "Nous n'avons pas pu charger les métriques."
        },
        unavailable: {
            metricId: "Métrique indisponible"
        },
        sentiments: {
            Positive: "Positif",
            Constructive: "Constructif"
        },
        types: {
            Officevibe: "Sondage éclair : questions d'Officevibe",
            Custom: "Sondage éclair : questions personnalisées",
            Onboarding: "Sondage sur l'intégration",
            NonSurvey: "Hors sondages",
            Smart: "Questions intelligentes"
        }
    },
    ExportMenu: {
        label: "Exporter",
        preparing: "Préparation",
        pdf: "Exporter en PDF",
        csv: "Exporter en CSV",
        csvFailed: "L'exportation n'a pas pu être préparée. Réessayez."
    },
    AnalyticsPageActions: {
        actions: "Analyser, exporter et partager",
        share: "Partager",
        preparing: "Préparation"
    },
    AskWorkleapAi: {
        label: "Analyser la performance",
        liveRegion: {
            collecting: "Collecte des résultats de performance"
        },
        noData: "Les résultats de performance n'ont pas pu être chargés, il n'y a donc rien à analyser. Réessayez.",
        failed: "Workleap AI est injoignable. Réessayez.",
        prompt: "Analyse les résultats de performance et dis-moi sur quoi me concentrer.",
        instructions:
            "La personne consulte une vue d'analytique de performance Workleap. Ses données se trouvent dans le contexte fourni. Appuie chaque affirmation sur ces données — n'invente jamais de chiffre, et dis clairement quand les données ne permettent pas de conclure. Lorsque plusieurs séries sont présentes, la première est la vue consultée et les autres sont ses comparaisons. Commence par ce qui ressort, puis par ce que cela signifie probablement, puis par ce qu'il faudrait examiner ensuite. Sois concis et ne répète pas les chiffres bruts.",
        filters: {
            directManagers_one: "les subordonnés de {{count}} gestionnaire direct",
            directManagers_other: "les subordonnés de {{count}} gestionnaires directs",
            reportingLine_one: "la ligne hiérarchique de {{count}} gestionnaire",
            reportingLine_other: "les lignes hiérarchiques de {{count}} gestionnaires",
            segment_one: "{{count}} segment",
            segment_other: "{{count}} segments",
            department_one: "{{count}} département",
            department_other: "{{count}} départements",
            gender_one: "{{count}} genre",
            gender_other: "{{count}} genres",
            workLocation_one: "{{count}} lieu de travail",
            workLocation_other: "{{count}} lieux de travail",
            roleLevel_one: "{{count}} niveau de poste",
            roleLevel_other: "{{count}} niveaux de poste",
            jobTitle_one: "{{count}} titre de poste",
            jobTitle_other: "{{count}} titres de poste",
            tenure: "ancienneté {{band}}",
            track: "filière {{track}}"
        }
    },
    ShareViewDialog: {
        title: "Partager",
        linkLabel: "Lien",
        copy: "Copier",
        copied: "Copié",
        audienceHint: "Les personnes ayant accès à cette audience peuvent ouvrir le lien.",
        emailCta: "Envoyer le lien par courriel",
        done: "Terminé",
        email: {
            subject: "{{surface}} dans Workleap",
            intro: "Voici la vue de performance."
        },
        surface: {
            performance: "Évaluations de performance",
            overview: "Scores",
            calibration: "Calibration",
            themes: "Thèmes"
        },
        view: {
            "nine-box": "Grille 9 cases"
        }
    },
    PerformanceOverviewPage: {
        heading: "Scores d'évaluation",
        csvUnavailable: "Disponible une fois les résultats chargés",
        seriesFilterLabel: "{{series}} · {{band}}",
        bandFilterTile: {
            below: "Score sous {{max}}",
            between: "Score entre {{min}} et {{max}}",
            atLeast: "Score de {{min}} et plus"
        },
        cellFilterTile: "{{cell}} : potentiel {{potential}}, performance {{performance}}",
        empty: {
            title: "Aucune personne évaluée pour le moment",
            description: "Dès que des évaluations seront partagées, les scores de votre organisation s'afficheront ici."
        },
        emptyFiltered: {
            title: "Aucune personne évaluée ne correspond à ces filtres",
            description: "Essayez de retirer un filtre pour élargir les résultats."
        },
        liveRegion: {
            loading: "Chargement des scores d'évaluation",
            loadingNineBox: "Chargement de la grille 9 cases"
        }
    },
    ScoreDistributionSection: {
        view: {
            groupLabel: "Vue des scores",
            histogram: "Distribution",
            nineBox: "Grille 9 cases"
        }
    },
    NineBoxGrid: {
        heading: "Performance et potentiel",
        axis: {
            high: "Élevé",
            potential: "Potentiel",
            low: "Faible",
            performance: "Performance",
            performanceBand: {
                Low: "faible",
                Medium: "moyenne",
                High: "élevée"
            },
            potentialBand: {
                Low: "faible",
                Medium: "moyen",
                High: "élevé"
            }
        },
        percentage: "{{percentage}} %",
        cell: {
            ariaLabel_one: "{{label}}, performance {{performance}} et potentiel {{potential}}, {{count}} personne, {{percentage}} %",
            ariaLabel_other: "{{label}}, performance {{performance}} et potentiel {{potential}}, {{count}} personnes, {{percentage}} %",
            seriesAriaLabel_one: "{{series}}, {{label}}, performance {{performance}} et potentiel {{potential}}, {{count}} personne",
            seriesAriaLabel_other: "{{series}}, {{label}}, performance {{performance}} et potentiel {{potential}}, {{count}} personnes",
            seriesNoDataAriaLabel: "{{series}}, {{label}}, performance {{performance}} et potentiel {{potential}}, aucune donnée"
        }
    },
    RevieweesTable: {
        ariaLabel: "Personnes évaluées",
        caption: {
            total_one: "{{count}} personne évaluée",
            total_other: "{{count}} personnes évaluées"
        },
        graphFilter: {
            removeLabel: "Retirer le filtre"
        },
        search: {
            placeholder: "Rechercher un membre",
            ariaLabel: "Rechercher des personnes évaluées"
        },
        noResults: "Aucune personne ne correspond à votre recherche.",
        noData: "Aucune personne évaluée à afficher pour cette sélection.",
        noScore: "Aucun score",
        comparisonNote: "Chaque personne appartient à un seul groupe : la comparaison s'applique donc à la distribution ci-dessus plutôt qu'à ces lignes.",
        unknownMember: "Membre inconnu",
        liveRegion: {
            loading: "Chargement des personnes évaluées",
            loadingMoreAnnouncement: "Chargement d'autres personnes évaluées",
            loadedMore_one: "{{count}} personne évaluée supplémentaire chargée",
            loadedMore_other: "{{count}} personnes évaluées supplémentaires chargées",
            results_one: "{{count}} résultat",
            results_other: "{{count}} résultats",
            sortedAscending: "Tableau trié par {{column}}, ordre croissant",
            sortedDescending: "Tableau trié par {{column}}, ordre décroissant"
        }
    },
    NineBoxCellLabels: {
        cell: {
            enigma: "Énigme",
            growth: "Croissance",
            stars: "Étoiles",
            dilemma: "Dilemme",
            core: "Noyau",
            "high-impact": "Fort impact",
            risk: "Risque",
            effective: "Efficace",
            trusted: "De confiance"
        }
    },
    PerformanceCalibrationPage: {
        heading: "Signaux de calibration"
    },
    CalibrationStatsRow: {
        inScope: {
            label: "Portée",
            people_one: "Personne évaluée",
            people_other: "Personnes évaluées",
            reviews_one: "Évaluation",
            reviews_other: "Évaluations"
        },
        average: {
            label: "Note moyenne",
            unit: "/ {{maxScale}}",
            ariaValue: "{{value}} sur {{maxScale}}",
            noScore: "Aucune note pour le moment"
        },
        distribution: {
            label: "Répartition des notes",
            percentage: "{{percentage}} %"
        },
        liveRegion: {
            loading: "Chargement du sommaire de calibration"
        }
    },
    ReviewerTypeAveragesStat: {
        label: "Note moyenne par type"
    },
    ReviewerTypes: {
        self: "Auto-évaluation",
        manager: "Gestionnaire",
        peer: "Pairs",
        upward: "Ascendante"
    },
    CalibrationDimensionSelector: {
        label: "Regrouper par"
    },
    CalibrationDimensions: {
        cycle: "Cycle d'évaluation",
        timeRange: "Intervalle",
        segment: "Segment",
        manager: "Gestionnaire direct",
        reportingLine: "Lignes hiérarchiques",
        function: "Département",
        gender: "Genre",
        workLocation: "Lieu de travail",
        roleLevel: "Niveau de poste",
        jobTitle: "Titre du poste",
        tenure: "Ancienneté",
        track: "Filière"
    },
    CalibrationSignalsTable: {
        title: "Signaux de calibration",
        ariaLabel: "Signaux de calibration",
        comparisonNote: "Chaque ligne correspond déjà à un seul élément de « {{dimension}} », donc la comparaison ne peut pas s'afficher par ligne. Comparez selon un autre critère que « {{dimension}} » pour la voir ici.",
        columns: {
            expand: "Afficher les détails du groupe"
        },
        expandGroup: "Afficher les détails de {{group}}",
        collapseGroup: "Masquer les détails de {{group}}",
        legend: {
            moderate: "À regarder",
            high: "Signal"
        },
        empty: "Aucun groupe ne compte assez d'évaluations pour être comparé.",
        emptyFiltered: "Aucun groupe ne correspond à ces filtres. Essayez de les élargir.",
        noValue: "Non disponible",
        unknownGroup: "Groupe inconnu",
        liveRegion: {
            loading: "Chargement des signaux de calibration",
            loadingMoreAnnouncement: "Chargement d'autres groupes",
            loadedMore_one: "{{count}} groupe supplémentaire chargé",
            loadedMore_other: "{{count}} groupes supplémentaires chargés",
            results_one: "{{count}} groupe",
            results_other: "{{count}} groupes",
            sortedAscending: "Tableau trié par {{column}}, ordre croissant",
            sortedDescending: "Tableau trié par {{column}}, ordre décroissant"
        }
    },
    CalibrationSignalInfo: {
        label: "À propos des signaux",
        title: "Comment un signal est attribué",
        intro: "Chaque groupe est lu de deux façons : l'écart interne de ses notes, et l'écart de sa moyenne par rapport à celle de l'entreprise.",
        high: "Un signal — écart interne d'environ {{spread}} ou moins, ou écart d'environ {{gap}} ou plus.",
        moderate: "À regarder — écart interne d'environ {{nearSpread}} ou moins, ou écart d'environ {{nearGap}} ou plus.",
        baseline: "Chaque écart est mesuré par rapport à la moyenne de l'entreprise de {{average}}.",
        floor: "En dessous de {{members}} personnes évaluées, aucune des deux valeurs n'est évaluée — une seule note les ferait trop varier. La ligne affiche tout de même ses propres chiffres."
    },
    CalibrationColumnInfo: {
        spread: {
            label: "À propos de l'écart interne",
            title: "Écart interne",
            descriptionNoBaseline: "À quel point les notes du groupe diffèrent entre elles. Un faible écart signifie qu'il a attribué presque la même note à tout le monde et ne distingue donc pas ses performances.",
            thresholds: "Signalé à environ {{high}} ou moins, et à regarder à environ {{moderate}} ou moins."
        },
        gap: {
            label: "À propos de l'écart",
            title: "Écart",
            description: "De combien la moyenne du groupe s'écarte de la moyenne de l'entreprise de {{average}}, au-dessus ou en dessous.",
            descriptionNoBaseline: "De combien la moyenne du groupe s'écarte de la moyenne de l'entreprise, au-dessus ou en dessous.",
            thresholds: "Signalé à environ {{high}} ou plus, et à regarder à environ {{moderate}} ou plus."
        },
        floor: "Les groupes de moins de {{members}} personnes évaluées ne sont jamais signalés — une seule note les ferait trop varier."
    },
    CalibrationGroupDetail: {
        spread: {
            title: "Répartition des scores",
            error: "Impossible de charger la répartition des scores de ce groupe.",
            retry: "Réessayer",
            liveRegion: {
                loading: "Chargement de la répartition des scores"
            }
        },
        people: {
            title: "Personnes"
        }
    },
    CalibrationGroupPeople: {
        empty: "Aucune personne évaluée dans ce groupe.",
        error: "Impossible de charger les personnes de ce groupe.",
        staleError: "Impossible d'actualiser — affichage des derniers résultats.",
        retry: "Réessayer",
        loadMore: "Afficher plus de personnes",
        noScore: "Non disponible",
        unknownMember: "Membre inconnu",
        liveRegion: {
            loading: "Chargement des personnes",
            loadedMore_one: "{{count}} personne de plus chargée",
            loadedMore_other: "{{count}} personnes de plus chargées"
        }
    },
    PerformanceThemesPage: {
        strengths: {
            title: "Forces"
        },
        watchOuts: {
            title: "Occasions de croissance"
        },
        empty: "Aucune donnée de sentiment disponible.",
        emptyFiltered: "Aucune donnée de sentiment ne correspond à ces filtres. Essayez de retirer un filtre pour élargir les résultats.",
        legend: {
            mentions_one: "{{mentions}} mention",
            mentions_other: "{{mentions}} mentions"
        },
        liveRegion: {
            loading: "Chargement des thèmes d'évaluation"
        }
    },
    ReviewThemes: buildReviewThemesNamespace("fr-CA", {
        ClarityOfExpectations: "Si les gens ont une compréhension claire de leur rôle, de leurs priorités et de ce à quoi ressemble le succès.",
        AccountabilityAndFollowThrough: "Dans quelle mesure les individus respectent leurs engagements et assument la responsabilité sans avoir besoin de rappels.",
        DecisionQualityAndExecutionDiscipline: "Dans quelle mesure les équipes prennent des décisions en temps opportun et font avancer le travail avec concentration et rigueur opérationnelle.",
        GrowthLearningAndSkillDevelopment: "Si les gens développent activement de nouvelles compétences, relèvent des défis et progressent dans leurs capacités.",
        FeedbackCulture: "Dans quelle mesure les équipes donnent et reçoivent des commentaires opportuns, spécifiques et exploitables qui favorisent l'amélioration.",
        IndividualLeadership: "Comment les individus influencent positivement les autres, élèvent l'équipe et modélisent des comportements exemplaires sans autorité formelle.",
        CollaborationAndCrossFunctionalEffectiveness: "Dans quelle mesure les gens travaillent bien entre les équipes pour partager le contexte, aligner les priorités et se débloquer mutuellement.",
        WorkloadAndSustainability: "Si la charge de travail, le rythme et les niveaux de stress favorisent une performance saine et durable dans le temps.",
        OutcomeOrientation: "Dans quelle mesure les individus se concentrent sur un impact significatif et relient leur travail aux résultats clients et commerciaux.",
        CommunicationAndTrust: "Si les gens se sentent en sécurité pour s'exprimer ouvertement, soulever des préoccupations rapidement et s'engager dans un dialogue honnête et constructif.",
        InnovationAndAdaptability: "Dans quelle mesure les individus génèrent de nouvelles solutions, s'adaptent au changement et naviguent dans l'ambiguïté avec un esprit de résolution de problèmes.",
        OwnershipAndInitiative: "Si les gens prennent des responsabilités de manière proactive, font progresser le travail de manière indépendante et agissent sans attendre de directives."
    }),
    PeriodFilterButton: {
        label: "Période",
        allCycles: "Tous les cycles",
        untitled: "Cycle sans titre",
        cycleCount_one: "{{count}} cycle",
        cycleCount_other: "{{count}} cycles",
        modes: "Type de période",
        byCycle: "Par cycle",
        byTimeRange: "Par intervalle"
    },
    TimeRangeFilterPanel: {
        title: "Intervalle",
        back: "Retour aux dimensions",
        customRange: "Intervalle personnalisé",
        rangeLabel: "Date de début et de fin",
        clear: "Effacer l'intervalle",
        invalidRange: "La date de fin ne peut pas précéder la date de début."
    },
    CycleFilterList: {
        title: "Cycles d'évaluation",
        back: "Retour aux dimensions",
        searchPlaceholder: "Rechercher un cycle d'évaluation",
        allCycles: "Tous les cycles",
        untitled: "Cycle sans titre",
        activity: {
            active: "Actif",
            locked: "Verrouillé"
        },
        empty: "Aucun cycle d'évaluation trouvé",
        noResults: "Aucun cycle d'évaluation ne correspond à votre recherche.",
        loading: "Chargement des cycles d'évaluation",
        liveRegion: {
            loadedMore_one: "{{count}} cycle d'évaluation supplémentaire chargé",
            loadedMore_other: "{{count}} cycles d'évaluation supplémentaires chargés",
            results_one: "{{count}} cycle d'évaluation trouvé",
            results_other: "{{count}} cycles d'évaluation trouvés"
        },
        error: "Impossible de charger les cycles d'évaluation. Veuillez réessayer.",
        staleError: "Impossible d'actualiser — affichage des derniers résultats."
    },
    ReviewCycles: {
        untitled: "Cycle sans titre"
    },
    ComparisonGroups: {
        unknownPerson: "Personne inconnue",
        unknownSegment: "Segment inconnu"
    },
    AnalyticsFiltersCard: {
        baseSeries: "Série {{letter}} : {{label}}",
        clearAll: "Tout effacer",
        seriesUsage_one: "{{count}} série de comparaison utilisée sur {{total}}",
        seriesUsage_other: "{{count}} séries de comparaison utilisées sur {{total}}"
    },
    ComparisonRowActions: {
        addComparison: "Ajouter une comparaison",
        remove: "Retirer la comparaison : {{label}}",
        capReached: "Maximum de {{count}} séries de comparaison atteint. Retirez-en une pour en ajouter une autre."
    },
    BreakdownButton: {
        label: "Ventiler par",
        addGroup: "Ajouter : {{dimension}}",
        hint_one: "{{count}} série de comparaison utilisée sur {{total}}.",
        hint_other: "{{count}} séries de comparaison utilisées sur {{total}}.",
        capReached: "Maximum de {{count}} séries de comparaison atteint. Retirez-en une pour en ajouter une autre.",
        done: "Terminé",
        liveRegion: {
            used_one: "{{count}} série de comparaison utilisée sur {{total}}",
            used_other: "{{count}} séries de comparaison utilisées sur {{total}}"
        }
    },
    BreakdownGroupRows: {
        seriesLabel: "Série {{letter}} : {{label}}",
        remove: "Retirer {{label}}"
    },
    ComparisonSeriesRow: {
        seriesLabel: "Série {{letter}} : {{label}}"
    },
    ComparisonFilterChips: {
        addFilter: "Ajouter un filtre",
        remove: "Retirer le filtre",
        anyValue: "Tous",
        valueCount_one: "{{count}} sélectionné",
        valueCount_other: "{{count}} sélectionnés",
        customValue: "Personnalisée",
        directManagers: {
            ariaLabel: "Gestionnaires directs : {{value}}"
        },
        reportingLine: {
            ariaLabel: "Lignes hiérarchiques : {{value}}"
        },
        segment: {
            ariaLabel: "Segment : {{value}}"
        },
        department: {
            ariaLabel: "Département : {{value}}"
        },
        gender: {
            ariaLabel: "Genre : {{value}}"
        },
        workLocation: {
            ariaLabel: "Lieu de travail : {{value}}"
        },
        roleLevel: {
            ariaLabel: "Niveau de poste : {{value}}"
        },
        jobTitle: {
            ariaLabel: "Titre du poste : {{value}}"
        },
        tenure: {
            ariaLabel: "Ancienneté : {{value}}"
        },
        track: {
            ariaLabel: "Filière : {{value}}"
        }
    },
    ComparisonSeriesLabels: {
        base: "Vue actuelle",
        fallback: "Comparaison {{number}}",
        partSeparator: " · ",
        managerCount_one: "{{count}} gestionnaire",
        managerCount_other: "{{count}} gestionnaires"
    },
    PeriodLabels: {
        cycles_one: "{{count}} cycle",
        cycles_other: "{{count}} cycles",
        range: "{{from}} – {{to}}",
        rangeFrom: "À partir du {{from}}",
        rangeTo: "Jusqu'au {{to}}",
        preset: {
            LastReview: "Dernière évaluation",
            PastNinetyDays: "90 derniers jours",
            LastYear: "Dernière année",
            LastTwoYears: "2 dernières années"
        }
    },
    ComparisonLegend: {
        ariaLabel: "Séries de comparaison",
        noData: "Aucune donnée"
    },
    EngagementHeatmapPage: {
        liveRegion: {
            loading: "Chargement de la carte thermique"
        },
        cellMode: {
            ariaLabel: "Affichage des cellules",
            score: "Score",
            variation: "Variation"
        },
        scoreDateLabel: "Date du score",
        sectionError: "La carte thermique n'a pas pu être chargée",
        sectionErrorReassurance: "Veuillez réessayer plus tard.",
        retry: "Réessayer",
        retrying: "Nouvelle tentative…"
    },
    EngagementHeatmapTable: {
        ariaLabel: "Carte thermique de l'engagement",
        heading: {
            title: "Vue comparative du sondage éclair",
            description: "Participation, eNPS et engagement par métrique pour l'équipe de chaque gestionnaire. Triez n'importe quelle colonne ou cliquez sur une métrique pour voir ses sous-métriques.",
            teamsCount_one: "{{count}} équipe",
            teamsCount_other: "{{count}} équipes"
        },
        columns: {
            manager: "Gestionnaire",
            participation: "Participation",
            enps: "eNPS",
            engagement: "Engagement global"
        },
        organizationRowLabel: "Organisation",
        benchmarkRowLabel: "Repère",
        benchmarkTooltip: "Choisissez l'industrie, la région et la taille d'entreprise qui correspondent le mieux à votre organisation. Cela vous permet de comparer vos résultats à ceux d'organisations similaires.",
        configureBenchmark: "Configurer l'industrie, la région et la taille d'entreprise du repère",
        notEnoughResponses: "Pas assez de réponses",
        noScore: "Aucun score",
        emptyState: "Aucune équipe ne correspond à la portée et à la période actuelles.",
        expandSubMetrics: "{{label}}, développer les sous-métriques",
        collapseSubMetrics: "{{label}}, réduire les sous-métriques",
        teamRowLabel: "Équipe du gestionnaire",
        sortBy: "Trier par {{label}}",
        sortAscending: "{{label}}, trié en ordre croissant. Activer pour trier en ordre décroissant.",
        sortDescending: "{{label}}, trié en ordre décroissant. Activer pour trier en ordre croissant.",
        showAllRows: "Afficher toutes les lignes ({{count}})",
        hideLowResponseTeams: "Masquer les équipes à faible taux de réponse",
        increased: "En hausse",
        decreased: "En baisse",
        heatTier: {
            extremeGood: "bien au-dessus des pairs",
            good: "au-dessus des pairs",
            bad: "en dessous des pairs",
            extremeBad: "bien en dessous des pairs"
        },
        variationTier: {
            moderate: "changement modéré",
            high: "changement important"
        },
        pagination: {
            summary: "Affichage de {{start}} à {{end}} sur {{total}}",
            previous: "Précédent",
            next: "Suivant",
            page: "Page {{page}} sur {{totalPages}}"
        },
        tooltip: {
            score: "Score {{value}}",
            asOfDate: "En date du {{date}}"
        },
        liveRegion: {
            sortChanged: "Trié par {{column}} {{direction}}",
            ascending: "croissant",
            descending: "décroissant"
        }
    },
    AtAGlanceSection: {
        heading: "En un coup d'œil",
        totalFeedbackLabel: "Total des commentaires",
        replyRateLabel: "Taux de réponse",
        unrepliedLabel: "Messages sans réponse",
        valueUnavailable: "Non disponible",
        deltaMessages_one: "{{count}} message",
        deltaMessages_other: "{{count}} messages",
        deltaPoints_one: "{{count}} pt",
        deltaPoints_other: "{{count}} pts",
        deltaUpMessages_one: "en hausse de {{count}} message",
        deltaUpMessages_other: "en hausse de {{count}} messages",
        deltaDownMessages_one: "en baisse de {{count}} message",
        deltaDownMessages_other: "en baisse de {{count}} messages",
        deltaUpPoints_one: "en hausse de {{count}} point",
        deltaUpPoints_other: "en hausse de {{count}} points",
        deltaDownPoints_one: "en baisse de {{count}} point",
        deltaDownPoints_other: "en baisse de {{count}} points",
        deltaSuffix: {
            "30d": "vs le mois précédent",
            "3m": "vs les 3 mois précédents",
            "6m": "vs les 6 mois précédents",
            quarter: "vs le trimestre précédent",
            year: "vs l'année précédente",
            custom: "vs la période précédente"
        },
        deltaFlatSuffix: {
            "30d": "Aucun changement par rapport au mois précédent",
            "3m": "Aucun changement par rapport aux 3 mois précédents",
            "6m": "Aucun changement par rapport aux 6 mois précédents",
            quarter: "Aucun changement par rapport au trimestre précédent",
            year: "Aucun changement par rapport à l'année précédente",
            custom: "Aucun changement par rapport à la période précédente"
        },
        emptyHeading: "Pas encore assez de commentaires",
        emptyBody: "Le total de vos commentaires, votre taux de réponse et le nombre de messages sans réponse apparaîtront ici quand il y aura suffisamment de commentaires.",
        errorHeading: "Impossible de charger l'aperçu des commentaires",
        errorBody: "Une erreur s'est produite lors du chargement du total de vos commentaires et de votre taux de réponse.",
        errorRetry: "Réessayer",
        errorRetrying: "En cours…",
        liveRegion: {
            loading: "Chargement de l'aperçu des commentaires"
        }
    },
    SentimentSection: {
        heading: "Sentiment",
        showTrend: "Voir la tendance",
        hideTrend: "Masquer la tendance",
        positiveLabel: "Positif",
        constructiveLabel: "Constructif",
        deltaPoints_one: "{{count}} pt",
        deltaPoints_other: "{{count}} pts",
        deltaUp_one: "en hausse de {{count}} point",
        deltaUp_other: "en hausse de {{count}} points",
        deltaDown_one: "en baisse de {{count}} point",
        deltaDown_other: "en baisse de {{count}} points",
        deltaSuffix: {
            "30d": "vs le mois précédent",
            "3m": "vs les 3 mois précédents",
            "6m": "vs les 6 mois précédents",
            quarter: "vs le trimestre précédent",
            year: "vs l'année précédente",
            custom: "vs la période précédente"
        },
        deltaFlatSuffix: {
            "30d": "Aucun changement par rapport au mois précédent",
            "3m": "Aucun changement par rapport aux 3 mois précédents",
            "6m": "Aucun changement par rapport aux 6 mois précédents",
            quarter: "Aucun changement par rapport au trimestre précédent",
            year: "Aucun changement par rapport à l'année précédente",
            custom: "Aucun changement par rapport à la période précédente"
        },
        chartTitle: "Sentiment au fil du temps",
        chartAlt: "Part des retours positifs, constructifs et sans sentiment sur la période sélectionnée",
        chartYAxisLabel: "Messages",
        legendPositive: "Positif",
        legendConstructive: "Constructif",
        legendNoSentiment: "Sans sentiment",
        emptyHeading: "Pas encore assez de commentaires",
        emptyBody: "Le sentiment positif et constructif, ainsi que son évolution, apparaîtront ici quand il y aura suffisamment de commentaires.",
        historyErrorBody: "Les données de tendance n'ont pas pu être chargées",
        errorHeading: "Impossible de charger les données de sentiment",
        errorBody: "Une erreur s'est produite lors du chargement de l'aperçu du sentiment.",
        errorRetry: "Réessayer",
        errorRetrying: "En cours…",
        liveRegion: {
            loading: "Chargement de l'aperçu du sentiment"
        }
    },
    BenchmarkOptionsDialog: {
        title: "Configuration du repère",
        description: "Choisissez l'industrie, la région et la taille d'entreprise qui correspondent le mieux à votre organisation. Cela vous permet de comparer vos résultats à ceux d'organisations similaires.",
        industryLabel: "Industrie",
        locationLabel: "Région",
        companySizeLabel: "Taille de l'organisation",
        cancel: "Annuler",
        confirm: "Confirmer",
        industries: {
            All: "Toutes les industries",
            NaturalResources: "Ressources naturelles",
            UtilitiesAndServices: "Services",
            Construction: "Construction",
            Manufacturing: "Fabrication",
            WholesaleAndRetailTrade: "Commerce en gros / de détail",
            TransportationAndWarehousing: "Transport et entreposage",
            InformationAndCulturalIndustries: "Industrie de l'information et industrie culturelle",
            FinancialServicesOrInsurance: "Services financiers et assurances",
            RealEstateAndRentalOrLeasing: "Immobilier, location et crédit-bail",
            ProfessionalScientificTechnicalServices: "Services professionnels, scientifiques et techniques",
            ManagementConsulting: "Conseil en gestion",
            EducationalServices: "Services éducatifs",
            HealthcareAndSocialAssistance: "Soins de santé et assistance sociale",
            ArtsEntertainmentAndRecreation: "Arts et divertissement",
            AccommodationAndFoodServices: "Hébergement, restauration, services alimentaires",
            PublicAndGovernmentAdministration: "Administration publique",
            ComputerSoftwareItServices: "Logiciels, TI et services",
            NonprofitOrganization: "Organisation à but non lucratif",
            MarketingAndAdvertising: "Marketing et publicité"
        },
        locations: {
            All: "Toutes les régions",
            APAC: "Asie",
            AustraliaAndNewZealand: "Australie et Nouvelle-Zélande",
            Canada: "Canada",
            Europe: "Europe",
            LatinAmerica: "Amérique latine",
            MiddleEastAndAfrica: "Moyen-Orient et Afrique",
            NorthAmerica: "Amérique du Nord",
            UnitedStates: "États-Unis"
        },
        companySizes: {
            AllRanges: "Toutes les tailles d'entreprise",
            OneTo99: "1 à 99 employés",
            OneHundredTo249: "100 à 249 employés",
            TwoHundredFiftyTo999: "250 à 999 employés",
            OneThousandAndMore: "Plus de 1000 employés"
        }
    },
    TopicSignalsCard: {
        title: "Signaux thématiques",
        newPill: "Nouveau",
        description:
            "Repérez les thèmes qui présentent des changements importants ou des tendances inhabituelles. Les thèmes proviennent d'une liste de 250 thèmes, élaborée par nos spécialistes en données et experts en RH. Lorsqu'un thème atteint le seuil minimum, il reçoit l'une des trois étiquettes suivantes :",
        legend: {
            Risk: "un enjeu existant ou émergent qui évolue dans la mauvaise direction.",
            Trending: "un élan positif notable ou un changement inhabituel.",
            New: "un thème qui est apparu pour la première fois au cours de cette période."
        },
        signals: {
            Risk: "Risque",
            Trending: "Tendance",
            New: "Nouveau"
        },
        rowDescription: {
            Risk: "Une hausse du feedback constructif sur ce thème. Consultez le feedback pour comprendre ce qui se passe.",
            Trending: "Le volume de feedback a nettement augmenté, mais le sentiment demeure stable.",
            New: "Ce thème vient d'apparaître. Il était absent du feedback de la période précédente. Voyez ce qui émerge."
        },
        columns: {
            signal: "Signal",
            topic: "Thème",
            volume: "Volume"
        },
        listLabel: "Signaux thématiques",
        volume_one: "{{formattedCount}} message",
        volume_other: "{{formattedCount}} messages",
        empty: {
            title: "Pas encore assez de feedback",
            description: "Les signaux de tendances par thème apparaîtront ici une fois qu'il y aura suffisamment de feedback."
        },
        sentimentFiltered: {
            title: "Aucun signal de thème à afficher",
            description: "Les signaux de thème ne sont pas disponibles lorsqu'un filtre de sentiment est appliqué. Retirez le filtre pour les afficher."
        },
        error: "Impossible de charger les signaux thématiques pour l'instant.",
        errorReassurance: "Veuillez réessayer plus tard.",
        retry: "Réessayer",
        retrying: "Nouvelle tentative…",
        liveRegion: {
            loading: "Chargement des signaux thématiques"
        }
    },
    GoodVibesDeltaChip: {
        variation: "{{percent}} % {{comparison}}",
        percent: "{{percent}} %",
        increased: "Hausse",
        decreased: "Baisse",
        noComparison: "Aucune comparaison disponible",
        comparison: {
            "7d": "par rapport aux 7 jours précédents",
            "30d": "par rapport aux 30 jours précédents",
            "3m": "par rapport aux 3 mois précédents",
            "6m": "par rapport aux 6 mois précédents",
            quarter: "par rapport au trimestre dernier",
            year: "par rapport à l'année dernière"
        }
    },
    GoodVibesStat: {
        percent: "{{percent}} %",
        points: "{{points}} pts",
        noValue: "–",
        notAvailable: "Non disponible",
        increased: "Hausse",
        decreased: "Baisse",
        comparison: {
            "7d": "par rapport aux 7 jours précédents",
            "30d": "par rapport aux 30 jours précédents",
            "3m": "par rapport aux 3 mois précédents",
            "6m": "par rapport aux 6 mois précédents",
            quarter: "par rapport au trimestre dernier",
            year: "par rapport à l'année dernière"
        }
    },
    GoodVibesTotalReceivedCard: {
        title: "Total des Bonnes Vibes envoyées",
        valueLabel: "Bonnes Vibes envoyées",
        period: {
            "7d": "7 derniers jours",
            "30d": "30 derniers jours",
            "3m": "3 derniers mois",
            "6m": "6 derniers mois",
            quarter: "Ce trimestre",
            year: "Cette année"
        },
        stats: {
            reach: "Portée",
            senderActivity: "Activité des expéditeurs",
            averageReceivedPerRecognizedPerson: "Reçues par destinataire"
        },
        error: "Impossible de charger les Bonnes Vibes envoyées pour l'instant.",
        errorReassurance: "Réessayez, ou revenez dans quelques minutes.",
        retry: "Réessayer",
        retrying: "Nouvelle tentative…",
        liveRegion: {
            loading: "Chargement des Bonnes Vibes envoyées"
        }
    },
    GoodVibesTrendChart: {
        yAxisLabel: "Bonnes Vibes",
        alt: "Graphique linéaire des Bonnes Vibes envoyées sur la période sélectionnée",
        week: "Semaine du {{date}}",
        quarter: "T{{quarter}}",
        legend: "Envoyées"
    }
};
